import { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Crown, Flame, BookmarkCheck, Loader2 } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [bookings, setBookings] = useState([]);

  useEffect(() => {
    if (user === false) {
      nav("/login");
      return;
    }
    if (user && typeof user === "object") {
      Promise.all([
        api.get("/dashboard").then((r) => r.data),
        api.get("/bookings/me").then((r) => r.data),
      ])
        .then(([d, b]) => {
          setData(d);
          setBookings(b);
        })
        .catch(() => {});
    }
  }, [user, nav]);

  if (!user || user === false || !data) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="animate-spin text-[var(--gold)]" />
      </div>
    );
  }

  const sub = data.subscription;

  return (
    <div className="min-h-screen grain">
      <Navbar />
      <div data-testid="user-dashboard-container" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="eyebrow">Member Portal</div>
            <h1 className="font-display text-4xl sm:text-5xl uppercase mt-2">
              Welcome back, <span className="gold-text">{user.name?.split(" ")[0] || "Champ"}</span>
            </h1>
          </div>
          <div
            data-testid="dashboard-membership-tier-badge"
            className={`px-4 py-2 rounded-full text-xs font-mono uppercase tracking-widest border ${
              sub ? "border-[var(--border-gold)] text-[var(--gold)]" : "border-[var(--border-subtle)] text-[var(--text-muted)]"
            }`}
          >
            {sub ? (
              <span className="inline-flex items-center gap-1"><Crown size={12} /> {sub.plan_name}</span>
            ) : (
              "No active tier"
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div data-testid="dashboard-upcoming-session-card" className="gr-card p-6">
            <div className="flex items-center gap-2 text-[var(--gold)]">
              <CalendarClock size={16} /><span className="eyebrow">Next Session</span>
            </div>
            {data.upcoming_session ? (
              <div className="mt-4">
                <div className="font-display text-2xl uppercase">{data.upcoming_session.service_name}</div>
                <div className="text-sm text-[var(--text-secondary)] mt-1">
                  {data.upcoming_session.date} · {data.upcoming_session.time_slot} · {data.upcoming_session.duration}
                </div>
              </div>
            ) : (
              <div className="mt-4 text-sm text-[var(--text-muted)]">No upcoming session. Book one on the home page.</div>
            )}
          </div>

          <div className="gr-card p-6">
            <div className="flex items-center gap-2 text-[var(--gold)]">
              <Flame size={16} /><span className="eyebrow">Streak</span>
            </div>
            <div className="mt-4 font-display text-6xl gold-text">{data.streak_days}</div>
            <div className="text-sm text-[var(--text-muted)] uppercase tracking-widest">days consistent</div>
          </div>

          <div data-testid="dashboard-saved-tips-list" className="gr-card p-6">
            <div className="flex items-center gap-2 text-[var(--gold)]">
              <BookmarkCheck size={16} /><span className="eyebrow">Saved Tips</span>
            </div>
            <div className="mt-3 space-y-2 max-h-40 overflow-y-auto">
              {data.saved_tips.length === 0 && (
                <div className="text-sm text-[var(--text-muted)]">No tips saved yet.</div>
              )}
              {data.saved_tips.map((s) => (
                <div key={s.tip_id} className="text-sm border-b border-[var(--border-subtle)] pb-2">
                  {s.tip.title}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10">
          <h2 className="font-display text-2xl uppercase mb-4">Recent bookings</h2>
          <div className="gr-card overflow-hidden">
            {bookings.length === 0 ? (
              <div className="p-6 text-sm text-[var(--text-muted)]">No bookings yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs font-mono uppercase tracking-widest text-[var(--text-muted)]">
                  <tr className="border-b border-[var(--border-subtle)]">
                    <th className="px-5 py-3">Service</th>
                    <th className="px-5 py-3">Date</th>
                    <th className="px-5 py-3">Time</th>
                    <th className="px-5 py-3">Duration</th>
                    <th className="px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-[var(--border-subtle)] last:border-b-0">
                      <td className="px-5 py-3">{b.service_name}</td>
                      <td className="px-5 py-3">{b.date}</td>
                      <td className="px-5 py-3">{b.time_slot}</td>
                      <td className="px-5 py-3">{b.duration}</td>
                      <td className="px-5 py-3 text-[var(--gold)] uppercase text-xs tracking-widest">{b.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
